import React, { useState, useEffect } from 'react';
import adminApi from '../services/adminApi';

function ABTestsPage() {
  const [abTests, setABTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    description: '',
    type: 'string',
    enabled: true,
    variants: [
      { name: 'control', value: '', weight: 50 },
      { name: 'variant', value: '', weight: 50 }
    ]
  });

  useEffect(() => {
    loadABTests();
  }, []);

  const loadABTests = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getABTests();
      setABTests(response.abTests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTest(null);
    setFormData({
      key: '',
      description: '',
      type: 'string',
      enabled: true,
      variants: [
        { name: 'control', value: '', weight: 50 },
        { name: 'variant', value: '', weight: 50 }
      ]
    });
    setIsModalOpen(true);
  };

  const openEditModal = (test) => {
    setEditingTest(test);
    setFormData({
      key: test.key,
      description: test.description || '',
      type: test.type || 'string',
      enabled: test.abTestConfig?.enabled ?? true,
      variants: test.abTestConfig?.variants || [
        { name: 'control', value: '', weight: 50 },
        { name: 'variant', value: '', weight: 50 }
      ]
    });
    setIsModalOpen(true);
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { name: `variant_${formData.variants.length}`, value: '', weight: 0 }
      ]
    });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 2) {
      alert('A/B test must have at least 2 variants');
      return;
    }
    const newVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: newVariants });
  };

  const handleSave = async () => {
    try {
      // Validate
      if (!formData.key) {
        alert('Key is required');
        return;
      }

      const totalWeight = formData.variants.reduce((sum, v) => sum + Number(v.weight), 0);
      if (totalWeight !== 100) {
        alert(`Variant weights must sum to 100% (currently ${totalWeight}%)`);
        return;
      }

      // Parse variant values based on type
      const variants = formData.variants.map(v => ({
        ...v,
        value: formData.type === 'number' ? Number(v.value) :
               formData.type === 'boolean' ? v.value === 'true' :
               v.value,
        weight: Number(v.weight)
      }));

      await adminApi.upsertABTest({
        key: formData.key,
        description: formData.description,
        type: formData.type,
        enabled: formData.enabled,
        variants
      });

      setIsModalOpen(false);
      await loadABTests();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading A/B tests...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1>A/B Tests</h1>
        <p>Configure and manage feature experiments</p>
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Active Tests</h3>
          <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
            + New A/B Test
          </button>
        </div>

        {error && <p style={{ color: '#dc2626' }}>{error}</p>}

        {abTests.length === 0 ? (
          <div className="admin-empty-state">
            <p>No A/B tests configured yet</p>
            <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
              Create your first A/B test
            </button>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Test Key</th>
                <th>Status</th>
                <th>Variants</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {abTests.map(test => (
                <tr key={test.key}>
                  <td>
                    <strong>{test.key}</strong>
                    {test.description && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {test.description}
                      </div>
                    )}
                  </td>
                  <td>
                    {test.abTestConfig?.enabled ? (
                      <span className="admin-badge admin-badge-success">Active</span>
                    ) : (
                      <span className="admin-badge admin-badge-warning">Disabled</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {test.abTestConfig?.variants?.map((v, i) => (
                        <span 
                          key={i} 
                          className="admin-badge admin-badge-info"
                          title={`Value: ${v.value}`}
                        >
                          {v.name}: {v.weight}%
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {new Date(test.updatedAt).toLocaleDateString()}
                  </td>
                  <td>
                    <button 
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => openEditModal(test)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="admin-modal-header">
              <h3>{editingTest ? 'Edit A/B Test' : 'New A/B Test'}</h3>
              <button className="admin-modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div className="admin-form-group">
              <label>Test Key</label>
              <input
                type="text"
                value={formData.key}
                onChange={e => setFormData({ ...formData, key: e.target.value })}
                placeholder="AB_TEST_FEATURE_NAME"
                disabled={!!editingTest}
              />
            </div>

            <div className="admin-form-group">
              <label>Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="What this test is measuring..."
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label>Value Type</label>
                <select
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>

              <div className="admin-form-group" style={{ flex: 1 }}>
                <label>Status</label>
                <select
                  value={String(formData.enabled)}
                  onChange={e => setFormData({ ...formData, enabled: e.target.value === 'true' })}
                >
                  <option value="true">Active</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>

            <div className="admin-form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Variants
                <button 
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  onClick={addVariant}
                >
                  + Add Variant
                </button>
              </label>
              
              {formData.variants.map((variant, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    marginBottom: '0.5rem',
                    alignItems: 'center'
                  }}
                >
                  <input
                    type="text"
                    value={variant.name}
                    onChange={e => handleVariantChange(index, 'name', e.target.value)}
                    placeholder="Variant name"
                    style={{ flex: 1 }}
                  />
                  {formData.type === 'boolean' ? (
                    <select
                      value={String(variant.value)}
                      onChange={e => handleVariantChange(index, 'value', e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : (
                    <input
                      type={formData.type === 'number' ? 'number' : 'text'}
                      value={variant.value}
                      onChange={e => handleVariantChange(index, 'value', e.target.value)}
                      placeholder="Value"
                      style={{ flex: 1 }}
                    />
                  )}
                  <input
                    type="number"
                    value={variant.weight}
                    onChange={e => handleVariantChange(index, 'weight', e.target.value)}
                    placeholder="Weight %"
                    style={{ width: '80px' }}
                    min="0"
                    max="100"
                  />
                  <span>%</span>
                  {formData.variants.length > 2 && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => removeVariant(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Total: {formData.variants.reduce((sum, v) => sum + Number(v.weight || 0), 0)}% 
                {formData.variants.reduce((sum, v) => sum + Number(v.weight || 0), 0) !== 100 && (
                  <span style={{ color: '#dc2626' }}> (must equal 100%)</span>
                )}
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave}>
                {editingTest ? 'Save Changes' : 'Create A/B Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ABTestsPage;
